// A site visit card is unusable to the backend without a state, so the address
// section never fails closed any more: if the map cannot name the pin we fall
// back, in order, to what the user typed, then to the state the card already
// carries (or the one on its IP plan), then to the closest place the coarse
// reverse lookup found. Only when *none* of those exist is the manual field
// mandatory — that is the case `manualStateRequired` reports to the form.

const clean = (value) => (typeof value === "string" ? value.trim() : "");

// The IP plan a card is planned under already names its governorate(s); for a
// brand new card that is the only "current state" we have.
export const ipPlanStateName = (selectedIpPlan) =>
  clean(
    selectedIpPlan?.ipPlanStates
      ?.map((planState) => planState?.stateName)
      .filter(Boolean)
      .join(", ")
  );

// Everything we can use when the map found no state and the user typed nothing.
export const fallbackState = ({ address, currentState, selectedIpPlan }) =>
  clean(currentState) ||
  clean(address?.closestState) ||
  ipPlanStateName(selectedIpPlan);

// What actually goes to LocationDetails.State.
export const resolveState = ({
  address,
  manualState,
  currentState,
  selectedIpPlan,
}) =>
  clean(address?.state) ||
  clean(manualState) ||
  fallbackState({ address, currentState, selectedIpPlan });

// True only when we would send an empty state — that is when the user *must*
// fill the manual field in.
export const manualStateRequired = ({ address, currentState, selectedIpPlan }) =>
  !clean(address?.state) &&
  !fallbackState({ address, currentState, selectedIpPlan });

// What goes to LocationDetails.Address: the exact address, else the closest
// place found, else the raw coordinates so the card still points somewhere.
export const resolveAddress = (address) => {
  const exact = clean(address?.parish) || clean(address?.closestAddress);
  if (exact) return exact;
  const { Latitude, Longitude } = address || {};
  if (Latitude === undefined || Longitude === undefined) return "";
  return `${Latitude}, ${Longitude}`;
};

// The pin itself is still required — without coordinates there is nothing to
// save. This replaces the old "address.parish is missing" gate, which blocked
// perfectly good cards whenever the geocoder came up short.
export const hasCoordinates = (address) =>
  address !== null &&
  typeof address === "object" &&
  !isNaN(parseFloat(address.Latitude)) &&
  !isNaN(parseFloat(address.Longitude));
